import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const EMPTY_FORM = {
  name: '',
  description: '',
  status: 'active',
};

const LaborClasses = () => {
  const [laborClasses, setLaborClasses] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingLaborClass, setEditingLaborClass] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchLaborClasses();
  }, []);

  const fetchLaborClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/labor-classes`);
      setLaborClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching labor classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLaborClasses = laborClasses.filter((laborClass) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      (laborClass.name || '').toLowerCase().includes(search) ||
      (laborClass.description || '').toLowerCase().includes(search);
    const matchesStatus = statusFilter === 'all' || laborClass.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingLaborClass(null);
    setShowCreateForm(false);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Labor class name is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingLaborClass) {
        const response = await axios.put(
          `${API_BASE_URL}/api/labor-classes/${editingLaborClass.vuid}`,
          formData
        );
        setLaborClasses(laborClasses.map((item) => (
          item.vuid === editingLaborClass.vuid ? response.data : item
        )));
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/labor-classes`, formData);
        setLaborClasses([...laborClasses, response.data].sort((a, b) => (
          (a.name || '').localeCompare(b.name || '')
        )));
      }
      resetForm();
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || 'An error occurred while saving the labor class' });
    }
  };

  const handleEdit = (laborClass) => {
    setShowCreateForm(false);
    setEditingLaborClass(laborClass);
    setFormData({
      name: laborClass.name || '',
      description: laborClass.description || '',
      status: laborClass.status || 'active',
    });
    setErrors({});
  };

  const handleDelete = async (vuid) => {
    if (!window.confirm('Are you sure you want to delete this labor class?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/labor-classes/${vuid}`);
      setLaborClasses(laborClasses.filter((item) => item.vuid !== vuid));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete labor class');
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 font-medium">{errors.submit}</p>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-lg font-semibold text-gray-900 mb-2">
            Labor Class *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: '' });
            }}
            className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Project Manager, Foreman, Carpenter"
          />
          {errors.name ? <p className="text-red-600 font-medium mt-2">{errors.name}</p> : null}
        </div>
        <div>
          <label htmlFor="status" className="block text-lg font-semibold text-gray-900 mb-2">
            Status
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="description" className="block text-lg font-semibold text-gray-900 mb-2">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-none"
            placeholder="Optional description"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-4 pt-2">
        <button type="button" onClick={resetForm} className="btn-claret">
          Cancel
        </button>
        <button type="submit" className="btn-claret">
          {editingLaborClass ? 'Update Labor Class' : 'Create Labor Class'}
        </button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl font-semibold text-gray-700">Loading labor classes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Labor Classes</h1>
            <p className="mt-2 text-gray-600">
              Maintain labor classes used on project labor rate schedules.
            </p>
          </div>
          <button
            onClick={showCreateForm ? resetForm : () => { resetForm(); setShowCreateForm(true); }}
            className="btn-claret"
            data-tooltip={showCreateForm ? 'Cancel' : 'Create Labor Class'}
          >
            {showCreateForm ? 'Cancel' : '+ Create New Labor Class'}
          </button>
        </div>

        {showCreateForm && !editingLaborClass ? (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Create New Labor Class</h2>
            {renderForm()}
          </div>
        ) : null}

        <div className="bg-white rounded-xl shadow-2xl p-8 mb-8 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="statusFilter" className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8 border border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Labor Classes ({filteredLaborClasses.length})</h2>
          {filteredLaborClasses.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {searchTerm || statusFilter !== 'all' ? 'No labor classes found' : 'No labor classes yet'}
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search criteria or filters.'
                  : 'Get started by creating your first labor class.'}
              </p>
              {!(searchTerm || statusFilter !== 'all') ? (
                <button onClick={() => setShowCreateForm(true)} className="btn-claret">
                  Create Labor Class
                </button>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor Class</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLaborClasses.map((laborClass) => (
                    editingLaborClass?.vuid === laborClass.vuid ? (
                      <tr key={laborClass.vuid}>
                        <td colSpan="4" className="px-4 py-4">
                          <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Labor Class</h3>
                          {renderForm()}
                        </td>
                      </tr>
                    ) : (
                      <tr key={laborClass.vuid}>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900">{laborClass.name}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{laborClass.description || '—'}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            laborClass.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {laborClass.status ? laborClass.status.charAt(0).toUpperCase() + laborClass.status.slice(1) : 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right whitespace-nowrap">
                          <button type="button" className="btn-claret mr-2" onClick={() => handleEdit(laborClass)}>
                            Edit
                          </button>
                          <button type="button" className="btn-claret" onClick={() => handleDelete(laborClass.vuid)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LaborClasses;
